import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth, useUser, RedirectToSignIn } from '@clerk/clerk-react';
import { apiClient } from '../api/client';
import type { AxiosError } from 'axios';

type CallbackState = 'loading' | 'success' | 'error' | 'signing-in';

interface APIError {
  error?: string;
  message?: string;
}

// Terminal-style typing animation component
function TerminalText({ text, delay = 0 }: { text: string; delay?: number }) {
  const [displayed, setDisplayed] = useState('');
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    const timeout = setTimeout(() => {
      let i = 0;
      const interval = setInterval(() => {
        if (i <= text.length) {
          setDisplayed(text.slice(0, i));
          i++;
        } else {
          clearInterval(interval);
          // Keep cursor blinking after typing
        }
      }, 30);
      return () => clearInterval(interval);
    }, delay);
    return () => clearTimeout(timeout);
  }, [text, delay]);

  useEffect(() => {
    const interval = setInterval(() => setShowCursor(c => !c), 530);
    return () => clearInterval(interval);
  }, []);

  return (
    <span className="font-mono">
      {displayed}
      <span className={`${showCursor ? 'opacity-100' : 'opacity-0'} transition-opacity`}>_</span>
    </span>
  );
}

// Animated connection line
function ConnectionLine({ active, success }: { active: boolean; success?: boolean }) {
  return (
    <div className="relative h-1 w-32 mx-6 overflow-hidden rounded-full bg-border/50">
      {active && !success && (
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary to-transparent animate-[shimmer_1s_infinite]" />
      )}
      {success && (
        <div className="absolute inset-0 bg-success animate-[expand_0.5s_ease-out_forwards]" />
      )}
    </div>
  );
}

// Animated icons
function TerminalIcon({ active }: { active: boolean }) {
  return (
    <div className={`relative p-4 rounded-xl border transition-all duration-500 ${
      active
        ? 'bg-card border-primary/50 shadow-[0_0_30px_-5px_hsl(var(--primary)/0.3)]'
        : 'bg-card/50 border-border'
    }`}>
      <svg
        viewBox="0 0 24 24"
        className={`w-8 h-8 transition-colors duration-500 ${active ? 'text-primary' : 'text-muted-foreground'}`}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="M6 9l4 3-4 3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 16h6" strokeLinecap="round" />
      </svg>
      {active && (
        <div className="absolute inset-0 rounded-xl bg-primary/5 animate-pulse" />
      )}
    </div>
  );
}

function LockIcon({ active, unlocked }: { active: boolean; unlocked: boolean }) {
  return (
    <div className={`relative p-4 rounded-xl border transition-all duration-500 ${
      active
        ? unlocked
          ? 'bg-card border-success/50 shadow-[0_0_30px_-5px_hsl(var(--success)/0.3)]'
          : 'bg-card border-primary/50 shadow-[0_0_30px_-5px_hsl(var(--primary)/0.3)]'
        : 'bg-card/50 border-border'
    }`}>
      <svg
        viewBox="0 0 24 24"
        className={`w-8 h-8 transition-all duration-500 ${
          unlocked
            ? 'text-success'
            : active
              ? 'text-primary'
              : 'text-muted-foreground'
        }`}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        {unlocked ? (
          <>
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 019.9-1" strokeLinecap="round" />
          </>
        ) : (
          <>
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </>
        )}
      </svg>
      {active && (
        <div className={`absolute inset-0 rounded-xl animate-pulse ${unlocked ? 'bg-success/5' : 'bg-primary/5'}`} />
      )}
    </div>
  );
}

function ErrorIcon() {
  return (
    <div className="relative p-4 rounded-xl border bg-card border-destructive/50 shadow-[0_0_30px_-5px_hsl(var(--destructive)/0.3)]">
      <svg
        viewBox="0 0 24 24"
        className="w-8 h-8 text-destructive"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M15 9l-6 6M9 9l6 6" strokeLinecap="round" />
      </svg>
    </div>
  );
}

export function CLICallbackPage() {
  const [searchParams] = useSearchParams();
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const { user } = useUser();
  const [pageState, setPageState] = useState<CallbackState>('loading');
  const [error, setError] = useState<string | null>(null);
  const authAttempted = useRef(false);

  const oauthState = searchParams.get('state');

  useEffect(() => {
    async function completeAuth() {
      if (!isLoaded) return;

      // If not signed in, we'll render RedirectToSignIn below
      if (!isSignedIn || !user) {
        setPageState('signing-in');
        return;
      }

      // Prevent double execution from React StrictMode
      if (authAttempted.current) return;
      authAttempted.current = true;

      // Validate we have the required state parameter
      if (!oauthState) {
        setError('Missing state parameter');
        setPageState('error');
        return;
      }

      try {
        // Get auth token
        const token = await getToken();
        if (!token) {
          setError('Failed to get auth token');
          setPageState('error');
          return;
        }

        // First sync the user to ensure they exist in the database
        await apiClient.post(
          '/api/v1/auth/sync',
          {
            email: user.primaryEmailAddress?.emailAddress,
            firstName: user.firstName,
            lastName: user.lastName,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        // Now complete the CLI auth
        await apiClient.post(
          '/api/v1/auth/cli/callback',
          { state: oauthState },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setPageState('success');
      } catch (err) {
        console.error('CLI callback error:', err);
        const axiosError = err as AxiosError<APIError>;
        // Handle nested error object from API response
        const errorData = axiosError.response?.data?.error;
        let message: string;
        if (typeof errorData === 'object' && errorData !== null && 'message' in errorData) {
          message = (errorData as { message: string }).message;
        } else if (typeof errorData === 'string') {
          message = errorData;
        } else {
          message = axiosError.response?.data?.message
            || axiosError.message
            || 'Unknown error';
        }
        setError(message);
        setPageState('error');
      }
    }

    completeAuth();
  }, [isLoaded, isSignedIn, user, getToken, oauthState]);

  // If not signed in, redirect to Clerk sign-in and come back
  if (isLoaded && !isSignedIn) {
    return <RedirectToSignIn />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      {/* Subtle radial glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full transition-all duration-1000 ${
          pageState === 'success'
            ? 'bg-success/5 blur-[100px]'
            : pageState === 'error'
              ? 'bg-destructive/5 blur-[100px]'
              : 'bg-primary/5 blur-[100px]'
        }`} />
      </div>

      <div className="relative w-full max-w-lg">
        {/* Main card */}
        <div className="glass rounded-2xl p-8 animate-scale-in">
          {/* Connection visualization */}
          <div className="flex items-center justify-center mb-8">
            <TerminalIcon active={pageState === 'loading' || pageState === 'signing-in'} />
            <ConnectionLine
              active={pageState === 'loading' || pageState === 'signing-in'}
              success={pageState === 'success'}
            />
            {pageState === 'error' ? (
              <ErrorIcon />
            ) : (
              <LockIcon
                active={true}
                unlocked={pageState === 'success'}
              />
            )}
          </div>

          {/* Status content */}
          <div className="text-center space-y-4">
            {pageState === 'loading' && (
              <>
                <h1 className="text-xl font-medium text-foreground">
                  <TerminalText text="Establishing secure connection..." />
                </h1>
                <div className="flex items-center justify-center gap-2 text-muted-foreground text-sm">
                  <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                  <span className="font-mono">Authenticating with KeyEnv</span>
                </div>
              </>
            )}

            {pageState === 'signing-in' && (
              <>
                <h1 className="text-xl font-medium text-foreground">
                  <TerminalText text="Redirecting to sign in..." />
                </h1>
                <p className="text-muted-foreground text-sm">
                  Please authenticate to continue
                </p>
              </>
            )}

            {pageState === 'success' && (
              <>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-success/10 border border-success/20 text-success text-sm font-medium mb-2">
                  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Connection established
                </div>
                <h1 className="text-xl font-medium text-foreground">
                  Authentication successful
                </h1>
                <p className="text-muted-foreground text-sm">
                  You can close this window and return to your terminal.
                </p>

                {/* Terminal hint */}
                <div className="mt-6 p-4 rounded-lg bg-card/80 border border-border font-mono text-sm text-left">
                  <div className="text-muted-foreground mb-1">$ keyenv login</div>
                  <div className="text-success">Successfully authenticated!</div>
                  <div className="text-muted-foreground/60 text-xs mt-2">
                    Your CLI session is now active
                  </div>
                </div>
              </>
            )}

            {pageState === 'error' && (
              <>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-destructive/10 border border-destructive/20 text-destructive text-sm font-medium mb-2">
                  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Connection failed
                </div>
                <h1 className="text-xl font-medium text-foreground">
                  Authentication failed
                </h1>
                <p className="text-muted-foreground text-sm mb-4">
                  Please try again from your terminal.
                </p>

                {error && (
                  <div className="p-4 rounded-lg bg-destructive/5 border border-destructive/20 font-mono text-sm text-left">
                    <div className="text-destructive/60 text-xs mb-1">Error details:</div>
                    <div className="text-destructive break-all">{error}</div>
                  </div>
                )}

                <div className="mt-4 p-4 rounded-lg bg-card/80 border border-border font-mono text-sm text-left">
                  <div className="text-muted-foreground">$ keyenv login</div>
                  <div className="text-muted-foreground/60 text-xs mt-2">
                    Run this command to try again
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Branding */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center gap-2 text-muted-foreground/60 text-sm">
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <span>KeyEnv</span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes expand {
          from { transform: scaleX(0); transform-origin: left; }
          to { transform: scaleX(1); transform-origin: left; }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
      `}</style>
    </div>
  );
}
